// Browser-compatible crypto using Web Crypto API
import { threatMonitor } from './threatMonitor';

export interface CSPConfiguration {
  enabled: boolean;
  nonceRotationInterval: number;
  reportUri?: string;
  reportOnly: boolean;
  directives: {
    defaultSrc: string[];
    scriptSrc: string[];
    styleSrc: string[];
    imgSrc: string[];
    connectSrc: string[];
    fontSrc: string[];
    objectSrc: string[];
    mediaSrc: string[];
    frameSrc: string[];
    childSrc: string[];
    workerSrc: string[];
    manifestSrc: string[];
    prefetchSrc: string[];
    formAction: string[];
    frameAncestors: string[];
    baseUri: string[];
    upgradeInsecureRequests: boolean;
    blockAllMixedContent: boolean;
  };
}

export interface CSPViolationReport {
  'document-uri': string;
  referrer: string;
  'violated-directive': string;
  'effective-directive': string;
  'original-policy': string;
  disposition: string;
  'blocked-uri': string;
  'line-number': number;
  'column-number': number;
  'source-file': string;
  'status-code': number;
  'script-sample': string;
}

export interface NonceInfo {
  value: string;
  createdAt: number;
  expiresAt: number;
}

class CSPManager {
  private configuration: CSPConfiguration;
  private currentNonce: NonceInfo;
  private nonceRotationTimer: NodeJS.Timeout | null = null;
  private observers: Map<string, (nonce: string) => void> = new Map();
  private violationCount: Map<string, number> = new Map();

  constructor() {
    this.configuration = this.getDefaultConfiguration();
    this.currentNonce = this.generateNonce();
    this.startNonceRotation();
  }

  /**
   * Get current nonce value
   */
  getCurrentNonce(): string {
    return this.currentNonce.value;
  }

  /**
   * Get nonce info including expiration
   */
  getNonceInfo(): NonceInfo {
    return { ...this.currentNonce };
  }

  /**
   * Generate CSP header value
   */
  generateCSPHeader(): string {
    const directives: string[] = [];
    const config = this.configuration.directives;

    // Add nonce to script-src and style-src
    const scriptSrc = [...config.scriptSrc];
    const styleSrc = [...config.styleSrc];

    if (this.configuration.enabled) {
      scriptSrc.push(`'nonce-${this.currentNonce.value}'`);
      styleSrc.push(`'nonce-${this.currentNonce.value}'`);
    }

    // Build directives
    if (config.defaultSrc.length > 0) {
      directives.push(`default-src ${config.defaultSrc.join(' ')}`);
    }

    directives.push(`script-src ${scriptSrc.join(' ')}`);
    directives.push(`style-src ${styleSrc.join(' ')}`);

    if (config.imgSrc.length > 0) {
      directives.push(`img-src ${config.imgSrc.join(' ')}`);
    }

    if (config.connectSrc.length > 0) {
      directives.push(`connect-src ${config.connectSrc.join(' ')}`);
    }

    if (config.fontSrc.length > 0) {
      directives.push(`font-src ${config.fontSrc.join(' ')}`);
    }

    if (config.objectSrc.length > 0) {
      directives.push(`object-src ${config.objectSrc.join(' ')}`);
    }

    if (config.mediaSrc.length > 0) {
      directives.push(`media-src ${config.mediaSrc.join(' ')}`);
    }

    if (config.frameSrc.length > 0) {
      directives.push(`frame-src ${config.frameSrc.join(' ')}`);
    }

    if (config.childSrc.length > 0) {
      directives.push(`child-src ${config.childSrc.join(' ')}`);
    }

    if (config.workerSrc.length > 0) {
      directives.push(`worker-src ${config.workerSrc.join(' ')}`);
    }

    if (config.manifestSrc.length > 0) {
      directives.push(`manifest-src ${config.manifestSrc.join(' ')}`);
    }

    if (config.formAction.length > 0) {
      directives.push(`form-action ${config.formAction.join(' ')}`);
    }

    if (config.frameAncestors.length > 0) {
      directives.push(`frame-ancestors ${config.frameAncestors.join(' ')}`);
    }

    if (config.baseUri.length > 0) {
      directives.push(`base-uri ${config.baseUri.join(' ')}`);
    }

    if (config.upgradeInsecureRequests) {
      directives.push('upgrade-insecure-requests');
    }

    if (config.blockAllMixedContent) {
      directives.push('block-all-mixed-content');
    }

    // Add report URI if configured
    if (this.configuration.reportUri) {
      directives.push(`report-uri ${this.configuration.reportUri}`);
    }

    return directives.join('; ');
  }

  /**
   * Get CSP header name based on configuration
   */
  getCSPHeaderName(): string {
    return this.configuration.reportOnly 
      ? 'Content-Security-Policy-Report-Only'
      : 'Content-Security-Policy';
  }

  /**
   * Process CSP violation report
   */
  processViolationReport(report: CSPViolationReport, request: { ip?: string; userAgent?: string; userId?: string }): void {
    const violationKey = `${report['violated-directive']}-${report['blocked-uri']}`;
    const currentCount = this.violationCount.get(violationKey) || 0;
    this.violationCount.set(violationKey, currentCount + 1);

    // Determine violation severity
    const severity = this.getViolationSeverity(report, currentCount + 1);

    // Log security event
    threatMonitor.logEvent({
      type: 'csp_violation',
      severity,
      source: 'csp_manager',
      userId: request.userId,
      ipAddress: request.ip,
      userAgent: request.userAgent,
      details: {
        violatedDirective: report['violated-directive'],
        effectiveDirective: report['effective-directive'],
        blockedUri: report['blocked-uri'],
        documentUri: report['document-uri'],
        sourceFile: report['source-file'],
        lineNumber: report['line-number'],
        columnNumber: report['column-number'],
        scriptSample: report['script-sample']?.substring(0, 100), // Limit sample size
        violationCount: currentCount + 1,
        disposition: report.disposition
      }
    });

    // Clean up old violations to prevent memory issues
    if (this.violationCount.size > 1000) {
      const entries = Array.from(this.violationCount.entries());
      entries.sort((a, b) => a[1] - b[1]); // Sort by count
      const toRemove = entries.slice(0, 200); // Remove lowest 200
      toRemove.forEach(([key]) => this.violationCount.delete(key));
    }
  }

  /**
   * Subscribe to nonce rotation events
   */
  subscribeToNonceRotation(id: string, callback: (nonce: string) => void): void {
    this.observers.set(id, callback);
  }

  /**
   * Unsubscribe from nonce rotation events
   */
  unsubscribeFromNonceRotation(id: string): void {
    this.observers.delete(id);
  }

  /**
   * Force nonce rotation
   */
  forceNonceRotation(): string {
    this.rotateNonce();
    return this.currentNonce.value;
  }

  /**
   * Update CSP configuration
   */
  updateConfiguration(config: Partial<CSPConfiguration>): void {
    this.configuration = { ...this.configuration, ...config };
    
    // Restart nonce rotation if interval changed
    if (config.nonceRotationInterval && config.nonceRotationInterval !== this.configuration.nonceRotationInterval) {
      this.stopNonceRotation();
      this.startNonceRotation();
    }

    threatMonitor.logEvent({
      type: 'system_error',
      severity: 'low',
      source: 'csp_manager',
      details: {
        action: 'configuration_updated',
        changes: Object.keys(config)
      }
    });
  }

  /**
   * Get CSP statistics
   */
  getStatistics(): {
    nonceRotations: number;
    violationsByDirective: Record<string, number>;
    topViolatedDirectives: Array<{ directive: string; count: number; }>;
    currentNonceAge: number;
  } {
    const violationsByDirective: Record<string, number> = {};
    
    Array.from(this.violationCount.entries()).forEach(([key, count]) => {
      const directive = key.split('-')[0];
      if (directive) {
        violationsByDirective[directive] = (violationsByDirective[directive] || 0) + count;
      }
    });

    const topViolatedDirectives = Object.entries(violationsByDirective)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([directive, count]) => ({ directive, count }));

    return {
      nonceRotations: Math.floor((Date.now() - this.currentNonce.createdAt) / this.configuration.nonceRotationInterval),
      violationsByDirective,
      topViolatedDirectives,
      currentNonceAge: Date.now() - this.currentNonce.createdAt
    };
  }

  /**
   * Generate cryptographically secure nonce using Web Crypto API
   */
  private generateNonce(): NonceInfo {
    // Use Web Crypto API for browser compatibility
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    const nonce = btoa(String.fromCharCode(...array));
    const now = Date.now();
    
    return {
      value: nonce,
      createdAt: now,
      expiresAt: now + this.configuration.nonceRotationInterval
    };
  }

  /**
   * Rotate nonce and notify observers
   */
  private rotateNonce(): void {
    const oldNonce = this.currentNonce.value;
    this.currentNonce = this.generateNonce();

    // Notify observers
    this.observers.forEach(callback => {
      try {
        callback(this.currentNonce.value);
      } catch (error) {
        console.error('Error notifying nonce rotation observer:', error);
      }
    });

    threatMonitor.logEvent({
      type: 'system_error',
      severity: 'low',
      source: 'csp_manager',
      details: {
        action: 'nonce_rotated',
        oldNonce: oldNonce.substring(0, 8) + '...',
        newNonce: this.currentNonce.value.substring(0, 8) + '...'
      }
    });
  }

  /**
   * Start automatic nonce rotation
   */
  private startNonceRotation(): void {
    if (this.nonceRotationTimer) {
      clearInterval(this.nonceRotationTimer);
    }

    this.nonceRotationTimer = setInterval(() => {
      this.rotateNonce();
    }, this.configuration.nonceRotationInterval);
  }

  /**
   * Stop automatic nonce rotation
   */
  private stopNonceRotation(): void {
    if (this.nonceRotationTimer) {
      clearInterval(this.nonceRotationTimer);
      this.nonceRotationTimer = null;
    }
  }

  /**
   * Get violation severity based on report and frequency
   */
  private getViolationSeverity(report: CSPViolationReport, count: number): 'low' | 'medium' | 'high' | 'critical' {
    // High severity for script-src violations (potential XSS)
    if (report['violated-directive'].startsWith('script-src')) {
      return count > 5 ? 'critical' : 'high';
    }

    // Medium severity for other executable content
    if (report['violated-directive'].startsWith('object-src') || 
        report['violated-directive'].startsWith('frame-src')) {
      return count > 10 ? 'high' : 'medium';
    }

    // Low severity for resource loading violations
    if (count > 20) {
      return 'medium';
    }

    return 'low';
  }

  /**
   * Get default CSP configuration
   */
  private getDefaultConfiguration(): CSPConfiguration {
    return {
      enabled: true,
      nonceRotationInterval: 300000, // 5 minutes
      reportOnly: process.env.NODE_ENV === 'development',
      directives: {
        defaultSrc: ["'self'"],
        scriptSrc: ["'self'", "'strict-dynamic'"],
        styleSrc: ["'self'", "'unsafe-inline'"], // Allow inline styles for CSS-in-JS
        imgSrc: ["'self'", "data:", "https:"],
        connectSrc: ["'self'", "https://en.wikipedia.org", "https://api.wikimedia.org"],
        fontSrc: ["'self'", "https://fonts.gstatic.com"],
        objectSrc: ["'none'"],
        mediaSrc: ["'self'"],
        frameSrc: ["'none'"],
        childSrc: ["'none'"],
        workerSrc: ["'self'"],
        manifestSrc: ["'self'"],
        prefetchSrc: ["'self'"],
        formAction: ["'self'"],
        frameAncestors: ["'none'"],
        baseUri: ["'self'"],
        upgradeInsecureRequests: process.env.NODE_ENV === 'production',
        blockAllMixedContent: process.env.NODE_ENV === 'production'
      }
    };
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.stopNonceRotation();
    this.observers.clear();
    this.violationCount.clear();
  }
}

// Export singleton instance
export const cspManager = new CSPManager();
export default cspManager;